import { type StatusConfig } from '@apps/web/components/ui/status_badge';
import { type PhoebeAgentRunStatus } from '@libraries/typescript/api_client/schema_types';

export const AUTOMATION_RUN_STATUS_LABELS = [
    'Ran',
    'Running',
    'Failed',
    'Waiting for you',
    'Cancelled',
    'Skipped',
] as const;

export type AutomationRunRowStatus =
    (typeof AUTOMATION_RUN_STATUS_LABELS)[number];

const RUN_STATUS_LABELS: Record<PhoebeAgentRunStatus, AutomationRunRowStatus> =
    {
        completed: 'Ran',
        idle: 'Ran',
        running: 'Running',
        failed: 'Failed',
        cancelled: 'Cancelled',
    };

const EVERY_RUN_STATUS: ReadonlyArray<PhoebeAgentRunStatus> = [
    'completed',
    'idle',
    'running',
    'failed',
    'cancelled',
];

// `shows` exists because needs_attention relabels a row after the read, so the
// server filter alone would leak "Waiting for you" rows into Ran. The same
// relabelling is why that facet has to read every status back.
const RUN_STATUS_FACETS: Record<
    AutomationRunRowStatus,
    Readonly<{
        readonly query: ReadonlyArray<PhoebeAgentRunStatus>;
        readonly shows: ReadonlyArray<AutomationRunRowStatus>;
    }>
> = {
    Ran: { query: ['completed', 'idle'], shows: ['Ran'] },
    Running: { query: ['running'], shows: ['Running'] },
    Failed: { query: ['failed'], shows: ['Failed'] },
    'Waiting for you': { query: EVERY_RUN_STATUS, shows: ['Waiting for you'] },
    Cancelled: { query: ['cancelled'], shows: ['Cancelled'] },
    Skipped: { query: [], shows: ['Skipped'] },
};

const STATUS_BADGES: Record<AutomationRunRowStatus, StatusConfig> = {
    Ran: { label: 'Ran', intent: 'positive' },
    Running: { label: 'Running', intent: 'info' },
    Failed: { label: 'Failed', intent: 'negative' },
    Skipped: { label: 'Skipped', intent: 'neutral' },
    Cancelled: { label: 'Cancelled', intent: 'neutral' },
    'Waiting for you': { label: 'Waiting for you', intent: 'warning' },
};

const SUPPRESSION_TOOLTIPS: Record<string, string> = {
    daily_cap: 'Hit the daily limit',
    hourly_cap: 'Hit the hourly limit',
};

export function automationRunRowStatus(
    status: PhoebeAgentRunStatus,
    needsAttention: boolean,
): AutomationRunRowStatus {
    if (needsAttention) {
        return 'Waiting for you';
    }
    return RUN_STATUS_LABELS[status];
}

export function automationRunStatusBadge(
    status: AutomationRunRowStatus,
): StatusConfig {
    return STATUS_BADGES[status];
}

export function automationSkipTooltip(
    suppressionReason: string | null,
): string | null {
    if (suppressionReason === null) {
        return null;
    }
    return SUPPRESSION_TOOLTIPS[suppressionReason] ?? null;
}

type FacetSelection = {
    readonly include: ReadonlyArray<string>;
    readonly exclude: ReadonlyArray<string>;
};

function readFacetSelection(value: unknown): FacetSelection {
    if (Array.isArray(value)) {
        return { include: value.map(String), exclude: [] };
    }
    if (value && typeof value === 'object') {
        const { include, exclude } = value as {
            include?: Array<unknown>;
            exclude?: Array<unknown>;
        };
        return {
            include: Array.isArray(include) ? include.map(String) : [],
            exclude: Array.isArray(exclude) ? exclude.map(String) : [],
        };
    }
    return { include: [], exclude: [] };
}

export function selectedAutomationRunStatuses(
    filterValue: unknown,
): ReadonlyArray<AutomationRunRowStatus> {
    const { include, exclude } = readFacetSelection(filterValue);
    const included =
        include.length > 0
            ? AUTOMATION_RUN_STATUS_LABELS.filter((label) =>
                  include.includes(label),
              )
            : AUTOMATION_RUN_STATUS_LABELS;
    return included.filter((label) => !exclude.includes(label));
}

export function automationRunStatusQuery(
    selected: ReadonlyArray<AutomationRunRowStatus>,
): ReadonlyArray<PhoebeAgentRunStatus> | undefined {
    const statuses = [
        ...new Set(selected.flatMap((label) => RUN_STATUS_FACETS[label].query)),
    ];
    if (statuses.length === EVERY_RUN_STATUS.length) {
        return undefined;
    }
    return statuses;
}

export function matchesAutomationRunStatusFacets(
    status: AutomationRunRowStatus,
    selected: ReadonlyArray<AutomationRunRowStatus>,
): boolean {
    return selected.some((label) =>
        RUN_STATUS_FACETS[label].shows.includes(status),
    );
}
